import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import { type GridConfig, useGridCols } from './useGridCols';

interface UseVirtualItems<T> {
    query: Omit<UseInfiniteQueryResult<unknown, unknown>, 'data'>;
    data: T[];
    gridConfig: GridConfig;
}

export const useVirtualItems = <T>(props: UseVirtualItems<T>) => {
    const { data, gridConfig } = props;
    const { isFetchingNextPage, hasNextPage, fetchNextPage } = props.query;

    const cols = useGridCols(gridConfig);
    const rows = useMemo(() => Math.ceil(data.length / cols), [data.length, cols]);

    const containerRef = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useWindowVirtualizer({
        count: rows + (hasNextPage ? 1 : 0),
        overscan: 5,
        estimateSize: () => 400, // estimate row height for accurate scrollbar dragging
        // measure dynamic row height, except in firefox because it measures table border height incorrectly
        measureElement:
            typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
                ? element => element.getBoundingClientRect().height
                : undefined,
        initialOffset: (() => {
            if (typeof sessionStorage === 'undefined') return 0;

            const item = sessionStorage.getItem('coasworld.scroll-offset');

            const value = z
                .number()
                .catch(0)
                .parse(JSON.parse(item ?? '{}'));

            return value;
        })(),
        initialMeasurementsCache: (() => {
            if (typeof sessionStorage === 'undefined') return;

            const item = sessionStorage.getItem('coasworld.measurements-cache');

            const value = z
                .array(
                    z.object({
                        key: z.union([z.number(), z.string(), z.bigint()]),
                        index: z.number(),
                        start: z.number(),
                        end: z.number(),
                        size: z.number(),
                        lane: z.number(),
                    }),
                )
                .optional()
                .catch(undefined)
                .parse(JSON.parse(item ?? '{}'));

            return value;
        })(),
        onChange: virtualizer => {
            sessionStorage.setItem(
                'coasworld.measurements-cache',
                JSON.stringify(Array.from(virtualizer.measurementsCache)),
            );
            sessionStorage.setItem('coasworld.scroll-offset', JSON.stringify(document.documentElement.scrollTop));
        },
    });
    const virtualRows = rowVirtualizer.getVirtualItems();

    // const fetchMoreOnBottomReached = useCallback(
    //     (containerRefElement?: HTMLDivElement | null) => {
    //         if (containerRefElement) {
    //             // fixme: usage of scrollTop for windownVirualizer doesn't work as it is always 0, insetal scroll of window and container marginTop should be used
    //             const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
    //
    //             // once the user has scrolled within 100px of the bottom of the table, fetch more data if we can
    //             if (scrollHeight - scrollTop - clientHeight < 100 && !isFetching && hasNextPage) {
    //                 void fetchNextPage();
    //             }
    //         }
    //     },
    //     [fetchNextPage, isFetching, hasNextPage]
    // );

    // a check on mount and after a fetch to see if the table is already scrolled to the bottom and immediately needs to fetch more data
    // useEffect(() => {
    //     fetchMoreOnBottomReached(containerRef.current);
    // }, [fetchMoreOnBottomReached]);

    // note: is used instead of callback before it'll be fixed
    useEffect(() => {
        const [lastItem] = virtualRows.reverse();

        if (!lastItem) {
            return;
        }

        if (lastItem.index >= rows - 1 && hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    }, [hasNextPage, fetchNextPage, isFetchingNextPage, virtualRows, rows]);

    return {
        containerRef,
        rowVirtualizer,
        rows,
        cols,
        virtualRows,
    };
};
